import type { JsonPrimitive } from '@plato/shared-util/json';
import type { SrPartialPackingTypeMap } from '../starrocks/types.ts';
import type { Prisma } from './generated/browser.ts';

export * from './generated/browser.ts';
export type { PrismaClient } from './generated/client.ts';
export type Decimal = InstanceType<typeof Prisma.Decimal>;

declare global {
    // eslint-disable-next-line @typescript-eslint/no-namespace -- required for merging
    namespace PrismaJson {
        export type EntityType = 'CUSTOMER';

        export type CrmEventLinkedEntity = {
            entityType: EntityType;
            entityId: string;
        }[];

        export type CrmCustomFieldsBase = Record<string, JsonPrimitive>;

        // Ordered content of one Ask Plato message. An assistant reply
        // alternates text parts (rendered as bubbles) with tool-call parts
        // (rendered as caption rows). A tool-call part stores the tool name,
        // how the call ended and, when the tool searched for something, the
        // search term it ran with (the model's argument) — never any other
        // argument, and never results (size, PII; Langfuse traces them). The
        // client captions the row from name and outcome. Rows written before GROW-365 carry a
        // `failed` flag and a server-rendered English `text` instead of
        // `outcome`; the read schema (webapp ai-chat-types.ts) tolerates them.
        export type AiChatMessagePart =
            | { type: 'text'; text: string }
            | {
                  type: 'toolCall';
                  id: string;
                  name: string;
                  outcome: 'done' | 'failed' | 'interrupted';
                  search?: string;
              };
        export type AiChatMessageParts = AiChatMessagePart[];
        // Set on assistant messages only. `interrupted`: the client aborted or
        // disconnected before the reply finished. `usage`: token counts the LLM
        // reported for the reply. `inputTokens` is from its last completed LLM
        // round counting system prompt,
        // tool definitions, replayed history, the question and that turn's
        // tool outputs. `outputTokens` is summed over all rounds. Absent when
        // the LLM reported no usage.
        export type AiChatMessageMetadata = {
            interrupted?: boolean;
            usage?: { inputTokens: number; outputTokens: number };
        };
        export type CrmDisplayConfig = import('../model/crm-config.ts').CrmDisplayConfig;
        export type CrmCustomFieldsConfig = import('../model/crm-config.ts').CrmCustomFieldsConfig;
        export type InsightVisibilityConfig = import('../model/insight-config.ts').InsightVisibilityConfig;
        export type DraftSalesOrderExternal = Record<string, unknown>;

        export type DraftSalesOrderAddress = {
            zip: string;
            city: string;
            line1: string;
            line2: string;
            line3: string;
            street: string;
            country: string;
            address_id: string;
            country_code: string;
        };

        export type DraftSalesOrderLineItem = {
            type: 'NORMAL' | 'NOTE';
            external: {
                encoded_price?: string;
                [key: string]: unknown;
            };
            line_item_id: string;
            /** Sales insight the product was suggested by in the q2c draft; triaged as interested on submit. */
            insight_id?: string;
            note_line_item?: {
                note: string;
            } | null;
            normal_line_item?: {
                note?: string;
                price: number;
                total: number;
                rebate: {
                    code: string;
                    amount: number;
                    percent: number;
                }[];
                quantity: number;
                storage_id: string;
                gross_margin: number;
                quantity_unit: string;
                shipping_type_id: string;
                product_variant_id: string;
                sales_units_per_package_unit?: number;
                partial_packing_type?: SrPartialPackingTypeMap;
                price_scale_factor?: number;
            } | null;
        }[];
    }
}
